import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { processesApi, processesKeys, type ListFilter } from './api';
import type { ProcessRow, ProcessStep, ProcessEdge, RaciRole, KpiRelationship, SeatKpiRole } from './types';

export function useProcesses(filter: ListFilter = {}) {
  return useQuery({
    queryKey: processesKeys.list(filter),
    queryFn: () => processesApi.list(filter),
    select: (d) => d.processes,
  });
}

export function useProcess(id: string | undefined) {
  return useQuery({
    queryKey: processesKeys.one(id || ''),
    queryFn: () => processesApi.getOne(id as string),
    enabled: !!id,
  });
}

export function useProcessGraph(id: string | undefined) {
  return useQuery({
    queryKey: processesKeys.graph(id || ''),
    queryFn: () => processesApi.getGraph(id as string),
    enabled: !!id,
  });
}

export function useProcessAudit(id: string | undefined) {
  return useQuery({
    queryKey: processesKeys.audit(id || ''),
    queryFn: () => processesApi.listAudit(id as string),
    select: (d) => d.audit,
    enabled: !!id,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: processesKeys.all });
}

export function useCreateProcess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ProcessRow>) => processesApi.create(body),
    onSuccess: () => { invalidateAll(qc); toast.success('Process created'); },
    onError: (e: Error) => toast.error('Failed to create process', { description: e.message }),
  });
}

export function useUpdateProcess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ProcessRow> & { bumpVersion?: boolean }) => processesApi.update(id, body),
    onSuccess: () => { invalidateAll(qc); toast.success('Process updated'); },
    onError: (e: Error) => toast.error('Update failed', { description: e.message }),
  });
}

export function useDeleteProcess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => processesApi.remove(id),
    onSuccess: () => { invalidateAll(qc); toast.success('Process archived'); },
    onError: (e: Error) => toast.error('Archive failed', { description: e.message }),
  });
}

export function useSaveGraph(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { steps: Array<Partial<ProcessStep> & { tempId?: string }>; edges: Array<Partial<ProcessEdge>>; flowConfig?: Record<string, unknown> }) =>
      processesApi.saveGraph(processId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: processesKeys.graph(processId) });
      qc.invalidateQueries({ queryKey: processesKeys.all });
      toast.success('Graph saved');
    },
    onError: (e: Error) => toast.error('Save failed', { description: e.message }),
  });
}

export function useSetStepKpis(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, items }: { stepId: string; items: Array<{ kpiId: string; relationship: KpiRelationship }> }) =>
      processesApi.setStepKpis(processId, stepId, items),
    onSuccess: () => qc.invalidateQueries({ queryKey: processesKeys.graph(processId) }),
    onError: (e: Error) => toast.error('Failed to set KPIs', { description: e.message }),
  });
}

export function useSetStepSeats(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, items }: { stepId: string; items: Array<{ seatId: string; role: RaciRole }> }) =>
      processesApi.setStepSeats(processId, stepId, items),
    onSuccess: () => qc.invalidateQueries({ queryKey: processesKeys.graph(processId) }),
    onError: (e: Error) => toast.error('Failed to set step seats', { description: e.message }),
  });
}

export function useSetProcessSeats(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{ seatId: string; role: RaciRole }>) =>
      processesApi.setProcessSeats(processId, items),
    onSuccess: () => qc.invalidateQueries({ queryKey: processesKeys.graph(processId) }),
    onError: (e: Error) => toast.error('Failed to set process seats', { description: e.message }),
  });
}

export function useSetKpiSeats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kpiId, items }: { kpiId: string; items: Array<{ seatId: string; role: SeatKpiRole }> }) =>
      processesApi.setKpiSeats(kpiId, items),
    onSuccess: () => qc.invalidateQueries({ queryKey: processesKeys.all }),
    onError: (e: Error) => toast.error('Failed to set KPI seats', { description: e.message }),
  });
}
